/**
 * Geofence register + editor (Wave 2.8).
 * Boundary drawing via `@/components/pmv/map` only.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { GeofenceEditorMap, type GeofenceDraftGeometry } from '@/components/pmv/map';
import pmvService from '@/services/pmv.service';
import type { PmvGeofence, PmvGeofenceCreatePayload } from '@/services/pmv.service';
import { useProjects } from '@/hooks/useProjects';
import { useAuthStore } from '@/store/authStore';

const { Text, Title } = Typography;

const GEOFENCE_TYPES = [
  'PROJECT',
  'SITE',
  'YARD',
  'DEPOT',
  'WORK_FRONT',
  'RESTRICTED_AREA',
  'CUSTOM',
];

function statusColor(s: string) {
  switch (s) {
    case 'ACTIVE':
      return 'success';
    case 'DRAFT':
      return 'default';
    case 'INACTIVE':
      return 'warning';
    case 'EXPIRED':
      return 'error';
    default:
      return 'default';
  }
}

function canManageGeofences(perms: string[] | undefined): boolean {
  const p = perms || [];
  return (
    p.includes('pmv.connected.geofence.manage') ||
    p.includes('pmv.admin') ||
    p.includes('pmv.manage')
  );
}

export const PmvGeofenceListPage: React.FC = () => {
  const navigate = useNavigate();
  const perms = useAuthStore((s) => s.user?.permissions);
  const manage = canManageGeofences(perms);
  const [status, setStatus] = useState<string | undefined>();
  const [type, setType] = useState<string | undefined>();

  const listQ = useQuery({
    queryKey: ['pmv-geofences', status, type],
    queryFn: () =>
      pmvService.listGeofences({
        status,
        type,
        include_inactive: true,
      }),
  });

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Geofences"
          documentNumber=""
          statusTag={<Tag color="blue">Wave 2</Tag>}
          subtitle={
            <Text type="secondary">
              Company-scoped boundaries for observed location context. Draft fences never generate operational
              events until activated.
            </Text>
          }
          primaryActions={
            <Space wrap>
              {manage ? (
                <Button type="primary" onClick={() => navigate('/asset/pmv/connected/geofences/new')}>
                  New geofence
                </Button>
              ) : null}
              <Button onClick={() => navigate('/asset/pmv/connected/map')}>Live Fleet Map</Button>
              <Button onClick={() => navigate('/asset/pmv/connected')}>Connected overview</Button>
            </Space>
          }
        />
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space wrap>
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 140 }}
            options={['DRAFT', 'ACTIVE', 'INACTIVE', 'EXPIRED'].map((v) => ({ value: v, label: v }))}
            onChange={setStatus}
          />
          <Select
            allowClear
            placeholder="Type"
            style={{ width: 160 }}
            options={GEOFENCE_TYPES.map((v) => ({ value: v, label: v }))}
            onChange={setType}
          />
        </Space>
        <Table<PmvGeofence>
          rowKey="id"
          loading={listQ.isLoading}
          dataSource={listQ.data ?? []}
          pagination={{ pageSize: 20 }}
          columns={[
            {
              title: 'Code',
              dataIndex: 'code',
              render: (c, r) => <Link to={`/asset/pmv/connected/geofences/${r.id}`}>{c}</Link>,
            },
            { title: 'Name', dataIndex: 'name' },
            { title: 'Type', dataIndex: 'type', width: 120 },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 100,
              render: (s) => <Tag color={statusColor(s)}>{s}</Tag>,
            },
            { title: 'Geometry', dataIndex: 'geometry_type', width: 100 },
            {
              title: 'Boundary',
              render: (_, r) =>
                r.geometry_type === 'CIRCLE'
                  ? `Circle · ${r.radius_m ?? '—'} m`
                  : `Polygon · rev ${r.geometry_revision}`,
            },
            {
              title: 'Monitoring',
              render: (_, r) =>
                [
                  r.entry_monitoring_enabled ? 'Entry' : null,
                  r.exit_monitoring_enabled ? 'Exit' : null,
                  r.dwell_monitoring_enabled ? 'Dwell' : null,
                ]
                  .filter(Boolean)
                  .join(', ') || '—',
            },
          ]}
        />
      </Space>
    </WorkspaceLayout>
  );
};

export const PmvGeofenceFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const perms = useAuthStore((s) => s.user?.permissions);
  const manage = canManageGeofences(perms);
  const [form] = Form.useForm();
  const geometryType = Form.useWatch('geometry_type', form) as 'CIRCLE' | 'POLYGON' | undefined;
  const [draftGeom, setDraftGeom] = useState<GeofenceDraftGeometry | null>(null);
  const [validateMsg, setValidateMsg] = useState<string | null>(null);

  const projectsQ = useProjects({ is_active: true });
  const detailQ = useQuery({
    queryKey: ['pmv-geofence', id],
    queryFn: () => pmvService.getGeofence(id!),
    enabled: !isNew && Boolean(id),
  });
  const revisionsQ = useQuery({
    queryKey: ['pmv-geofence-revisions', id],
    queryFn: () => pmvService.listGeofenceRevisions(id!),
    enabled: !isNew && Boolean(id),
  });
  const occupancyQ = useQuery({
    queryKey: ['pmv-geofence-occupancy', id],
    queryFn: () => pmvService.getGeofenceOccupancy(id!),
    enabled: !isNew && Boolean(id) && detailQ.data?.status === 'ACTIVE',
    retry: false,
  });

  useEffect(() => {
    const row = detailQ.data;
    if (!row) return;
    form.setFieldsValue({
      code: row.code,
      name: row.name,
      type: row.type,
      geometry_type: row.geometry_type,
      project_id: row.project_id,
      site_ref: row.site_ref,
      work_front_ref: row.work_front_ref,
      purpose: row.purpose,
      description: row.description,
      entry_monitoring_enabled: row.entry_monitoring_enabled,
      exit_monitoring_enabled: row.exit_monitoring_enabled,
      dwell_monitoring_enabled: row.dwell_monitoring_enabled,
      movement_monitoring_enabled: row.movement_monitoring_enabled,
      priority: row.priority,
    });
    if (row.geometry_type === 'CIRCLE' && row.center_lat != null && row.center_lon != null) {
      setDraftGeom({
        geometry_type: 'CIRCLE',
        center_lat: Number(row.center_lat),
        center_lon: Number(row.center_lon),
        radius_m: Number(row.radius_m) || 200,
      });
    } else if (row.geometry_type === 'POLYGON' && row.geometry_json?.coordinates) {
      setDraftGeom({
        geometry_type: 'POLYGON',
        geometry_json: {
          type: 'Polygon',
          coordinates: row.geometry_json.coordinates,
        },
      });
    }
  }, [detailQ.data, form]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const values = await form.validateFields();
      if (!draftGeom) throw new Error('Define a boundary on the map');
      const payload: PmvGeofenceCreatePayload = {
        code: values.code,
        name: values.name,
        type: values.type,
        geometry_type: values.geometry_type,
        project_id: values.project_id || null,
        site_ref: values.site_ref || null,
        work_front_ref: values.work_front_ref || null,
        purpose: values.purpose || null,
        description: values.description || null,
        entry_monitoring_enabled: values.entry_monitoring_enabled !== false,
        exit_monitoring_enabled: values.exit_monitoring_enabled !== false,
        dwell_monitoring_enabled: Boolean(values.dwell_monitoring_enabled),
        movement_monitoring_enabled: Boolean(values.movement_monitoring_enabled),
        priority: values.priority,
        ...(draftGeom.geometry_type === 'CIRCLE'
          ? {
              center_lat: draftGeom.center_lat,
              center_lon: draftGeom.center_lon,
              radius_m: draftGeom.radius_m,
            }
          : { geometry_json: draftGeom.geometry_json }),
      };
      if (isNew) return pmvService.createGeofence(payload);
      return pmvService.patchGeofence(id!, { ...payload, change_reason: 'editor update' });
    },
    onSuccess: (row) => {
      message.success(isNew ? 'Geofence created as DRAFT' : 'Geofence updated');
      qc.invalidateQueries({ queryKey: ['pmv-geofences'] });
      navigate(`/asset/pmv/connected/geofences/${row.id}`);
    },
    onError: (e: Error) => message.error(e.message || 'Save failed'),
  });

  const activateMut = useMutation({
    mutationFn: () => pmvService.activateGeofence(id!),
    onSuccess: () => {
      message.success('Geofence activated');
      qc.invalidateQueries({ queryKey: ['pmv-geofence', id] });
      qc.invalidateQueries({ queryKey: ['pmv-geofences'] });
    },
    onError: (e: Error) => message.error(e.message || 'Activate failed'),
  });

  const deactivateMut = useMutation({
    mutationFn: () => pmvService.deactivateGeofence(id!),
    onSuccess: () => {
      message.success('Geofence deactivated');
      qc.invalidateQueries({ queryKey: ['pmv-geofence', id] });
      qc.invalidateQueries({ queryKey: ['pmv-geofences'] });
    },
    onError: (e: Error) => message.error(e.message || 'Deactivate failed'),
  });

  const validateMut = useMutation({
    mutationFn: async () => {
      if (!draftGeom) throw new Error('No geometry to validate');
      return pmvService.validateGeofenceGeometry(
        draftGeom.geometry_type === 'CIRCLE'
          ? {
              geometry_type: 'CIRCLE',
              center_lat: draftGeom.center_lat,
              center_lon: draftGeom.center_lon,
              radius_m: draftGeom.radius_m,
            }
          : {
              geometry_type: 'POLYGON',
              geometry_json: draftGeom.geometry_json,
            }
      );
    },
    onSuccess: (r) => {
      setValidateMsg(r.message || 'Geometry valid');
      message.success('Geometry valid');
    },
    onError: (e: Error) => {
      setValidateMsg(e.message);
      message.error(e.message || 'Invalid geometry');
    },
  });

  const projectOptions = useMemo(
    () =>
      (projectsQ.data ?? []).map((p) => ({
        value: p.id,
        label: `${p.project_number || ''} ${p.project_name || ''}`.trim() || p.id,
      })),
    [projectsQ.data]
  );

  const row = detailQ.data;
  const readOnly = !manage;

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={isNew ? 'New geofence' : row?.name || 'Geofence'}
          documentNumber={row?.code || ''}
          statusTag={row ? <Tag color={statusColor(row.status)}>{row.status}</Tag> : <Tag>DRAFT</Tag>}
          subtitle={
            <Text type="secondary">
              Basic info → business context → boundary → monitoring → validate → activate. Location remains
              evidence only.
            </Text>
          }
          primaryActions={
            <Space wrap>
              <Button onClick={() => navigate('/asset/pmv/connected/geofences')}>Back to list</Button>
              {!isNew && row?.status === 'DRAFT' && manage ? (
                <Button type="primary" loading={activateMut.isPending} onClick={() => activateMut.mutate()}>
                  Activate
                </Button>
              ) : null}
              {!isNew && row?.status === 'ACTIVE' && manage ? (
                <Button danger loading={deactivateMut.isPending} onClick={() => deactivateMut.mutate()}>
                  Deactivate
                </Button>
              ) : null}
            </Space>
          }
        />
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {!manage ? (
          <Alert type="info" showIcon message="View only — geofence.manage required to edit or activate." />
        ) : null}

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={10}>
            <Card title="1. Basic information" size="small">
              <Form
                form={form}
                layout="vertical"
                initialValues={{
                  geometry_type: 'CIRCLE',
                  type: 'SITE',
                  entry_monitoring_enabled: true,
                  exit_monitoring_enabled: true,
                  dwell_monitoring_enabled: false,
                  movement_monitoring_enabled: false,
                }}
                disabled={readOnly}
              >
                <Form.Item
                  name="code"
                  label="Code"
                  rules={[{ required: true, message: 'Code required' }]}
                >
                  <Input placeholder="e.g. RUW-SITE-N" disabled={!isNew} />
                </Form.Item>
                <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                  <Select options={GEOFENCE_TYPES.map((t) => ({ value: t, label: t }))} />
                </Form.Item>
                <Form.Item name="geometry_type" label="Boundary shape" rules={[{ required: true }]}>
                  <Select
                    options={[
                      { value: 'CIRCLE', label: 'Circle' },
                      { value: 'POLYGON', label: 'Polygon' },
                    ]}
                    onChange={() => setDraftGeom(null)}
                  />
                </Form.Item>
                <Form.Item name="purpose" label="Purpose">
                  <Input placeholder="Equipment location monitoring" />
                </Form.Item>
                <Form.Item name="description" label="Description">
                  <Input.TextArea rows={2} />
                </Form.Item>
                <Form.Item name="priority" label="Priority (lower = higher)">
                  <InputNumber min={1} max={999} style={{ width: '100%' }} />
                </Form.Item>

                <Title level={5}>2. Business context</Title>
                <Form.Item name="project_id" label="Project">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    loading={projectsQ.isLoading}
                    options={projectOptions}
                    placeholder="Optional project link"
                  />
                </Form.Item>
                <Form.Item name="site_ref" label="Site reference">
                  <Input placeholder="e.g. Site North" />
                </Form.Item>
                <Form.Item name="work_front_ref" label="Work front reference">
                  <Input placeholder="e.g. WF-03" />
                </Form.Item>

                <Title level={5}>4. Monitoring</Title>
                <Form.Item name="entry_monitoring_enabled" valuePropName="checked">
                  <Checkbox>Entry</Checkbox>
                </Form.Item>
                <Form.Item name="exit_monitoring_enabled" valuePropName="checked">
                  <Checkbox>Exit</Checkbox>
                </Form.Item>
                <Form.Item name="dwell_monitoring_enabled" valuePropName="checked">
                  <Checkbox>Dwell</Checkbox>
                </Form.Item>
                <Form.Item name="movement_monitoring_enabled" valuePropName="checked">
                  <Checkbox>Movement</Checkbox>
                </Form.Item>
              </Form>
            </Card>
          </Col>

          <Col xs={24} lg={14}>
            <Card
              title="3. Define boundary"
              size="small"
              extra={
                <Space>
                  <Button
                    size="small"
                    loading={validateMut.isPending}
                    onClick={() => validateMut.mutate()}
                    disabled={!draftGeom}
                  >
                    5. Validate
                  </Button>
                  {manage ? (
                    <Button
                      type="primary"
                      size="small"
                      loading={saveMut.isPending}
                      onClick={() => saveMut.mutate()}
                    >
                      {isNew ? 'Save draft' : 'Save changes'}
                    </Button>
                  ) : null}
                </Space>
              }
            >
              <GeofenceEditorMap
                geometryType={geometryType || 'CIRCLE'}
                value={draftGeom}
                onChange={setDraftGeom}
                readOnly={readOnly}
                height={420}
              />
              {validateMsg ? (
                <Alert style={{ marginTop: 8 }} type="info" showIcon message={validateMsg} />
              ) : null}
              {draftGeom?.geometry_type === 'CIRCLE' ? (
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  Radius {draftGeom.radius_m} m · center {draftGeom.center_lat.toFixed(5)},{' '}
                  {draftGeom.center_lon.toFixed(5)}
                </Text>
              ) : null}
            </Card>

            {!isNew && row ? (
              <Card title="6–7. Review / activate" size="small" style={{ marginTop: 12 }}>
                <Descriptions size="small" column={1}>
                  <Descriptions.Item label="Status">
                    <Tag color={statusColor(row.status)}>{row.status}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Geometry revision">{row.geometry_revision}</Descriptions.Item>
                  <Descriptions.Item label="Activated">
                    {row.activated_at || 'Not activated'}
                  </Descriptions.Item>
                </Descriptions>
                <Text type="secondary">
                  Activation validates geometry and project references. Draft does not emit ENTER/EXIT events.
                </Text>
              </Card>
            ) : null}

            {!isNew && occupancyQ.data ? (
              <Card title="Current occupancy (observed)" size="small" style={{ marginTop: 12 }}>
                <Space wrap>
                  <Tag color="success">Current {occupancyQ.data.counts.CURRENT}</Tag>
                  <Tag>Stale {occupancyQ.data.counts.STALE}</Tag>
                  <Tag>Unknown {occupancyQ.data.counts.UNKNOWN}</Tag>
                </Space>
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  {occupancyQ.data.note}
                </Text>
              </Card>
            ) : null}

            {!isNew ? (
              <Card title="Geometry revisions" size="small" style={{ marginTop: 12 }}>
                <Table
                  size="small"
                  rowKey="id"
                  loading={revisionsQ.isLoading}
                  dataSource={revisionsQ.data ?? []}
                  pagination={false}
                  columns={[
                    { title: 'Rev', dataIndex: 'revision', width: 60 },
                    { title: 'Type', dataIndex: 'geometry_type', width: 90 },
                    { title: 'Reason', dataIndex: 'change_reason' },
                    { title: 'At', dataIndex: 'created_at', width: 180 },
                  ]}
                />
              </Card>
            ) : null}
          </Col>
        </Row>
      </Space>
    </WorkspaceLayout>
  );
};

export default PmvGeofenceListPage;
